import {v4 as uuidv4} from 'uuid';
import {assertEvent, assign, setup} from 'xstate';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  applyAddLayer,
  applyInitLayers,
  applyRemoveLayer,
  applySetActiveLayer,
  applyUpdateLayerName,
  buildInitialLayerContext,
  LayerBase,
  LayerContextBase,
  LayerMachineInputBase,
} from 'samples/plate-maps/layer-machine/sharedLayerActions';
import {MetadataLayerInfo, PLATE_MAP_MAX_METADATA_LAYERS} from 'samples/plate-maps/PlateMapConstants';

const MACHINE_NAME = 'MetadataLayerMachine';

export interface MetadataLayerData extends MetadataLayerInfo, LayerBase {
  /**
   * Transient "brush" value typed in the settings panel and applied to selected wells. Held per
   * layer (like concentration settings) so it can't leak across layers, and `undefined` means the
   * user hasn't entered a value yet. Not persisted to the plate map's layer info.
   */
  draftValue?: string | number | boolean | null;
}

export type MetadataLayerEvents =
  | {type: 'initLayers'; layers: Array<MetadataLayerData>}
  | {type: 'addLayer'; layerId: string; name: string; layerType: LeafType}
  | {type: 'removeLayer'; layerId: string}
  | {type: 'setActiveLayer'; layerId: string | null}
  | {type: 'updateLayerName'; layerId: string; name: string}
  | {type: 'updateLayerType'; layerId: string; layerType: LeafType}
  | {type: 'setDraftValue'; layerId: string; value: string | number | boolean | null};

export type MetadataLayerContext = LayerContextBase<MetadataLayerData>;

export type MetadataLayerMachineInput = LayerMachineInputBase<MetadataLayerData>;

export function createMetadataLayerId(): string {
  return `metadata-layer-${uuidv4()}`;
}

export const MetadataLayerMachine = setup({
  types: {
    input: {} as MetadataLayerMachineInput,
    context: {} as MetadataLayerContext,
    events: {} as MetadataLayerEvents,
  },
  actions: {
    initLayers: assign(({context, event}) => {
      assertEvent(event, 'initLayers');
      return applyInitLayers(context, event.layers, PLATE_MAP_MAX_METADATA_LAYERS, MACHINE_NAME);
    }),

    addLayer: assign(({context, event}) => {
      assertEvent(event, 'addLayer');
      const newLayer: MetadataLayerData = {layerId: event.layerId, name: event.name, type: event.layerType};
      return applyAddLayer(context, newLayer, PLATE_MAP_MAX_METADATA_LAYERS, MACHINE_NAME);
    }),

    removeLayer: assign(({context, event}) => {
      assertEvent(event, 'removeLayer');
      return applyRemoveLayer(context, event.layerId);
    }),

    setActiveLayer: assign(({context, event}) => {
      assertEvent(event, 'setActiveLayer');
      return applySetActiveLayer(context, event.layerId);
    }),

    updateLayerName: assign(({context, event}) => {
      assertEvent(event, 'updateLayerName');
      return applyUpdateLayerName(context, event.layerId, event.name);
    }),

    updateLayerType: assign(({context, event}) => {
      assertEvent(event, 'updateLayerType');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      const layers = new Map(context.layers);
      // Drop the draft value: a value typed for the previous type isn't meaningful after the type
      // changes, and the settings panel re-derives its input from the (now empty) draft.
      layers.set(event.layerId, {...layer, type: event.layerType, draftValue: undefined});
      return {...context, layers};
    }),

    setDraftValue: assign(({context, event}) => {
      assertEvent(event, 'setDraftValue');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      const layers = new Map(context.layers);
      layers.set(event.layerId, {...layer, draftValue: event.value});
      return {...context, layers};
    }),
  },
}).createMachine({
  context: ({input}) => buildInitialLayerContext(input, PLATE_MAP_MAX_METADATA_LAYERS, MACHINE_NAME),

  id: 'MetadataLayer',
  initial: 'idle',

  states: {
    idle: {
      on: {
        initLayers: {actions: 'initLayers'},
        addLayer: {actions: 'addLayer'},
        removeLayer: {actions: 'removeLayer'},
        setActiveLayer: {actions: 'setActiveLayer'},
        updateLayerName: {actions: 'updateLayerName'},
        updateLayerType: {actions: 'updateLayerType'},
        setDraftValue: {actions: 'setDraftValue'},
      },
    },
  },
});
